// client/src/coms/components/LoginButton.js
import React, { useRef, useState } from 'react';
import { AUTHORITY_ORIGIN } from '../comsApi';
import { openExternalUrl } from '../auth/providerAuth';

function getLocalOrigin() {
  try {
    if (typeof window === 'undefined' || !window.location) return '';
    return String(window.location.origin || '').trim();
  } catch {
    return '';
  }
}

function joinUrl(base, path) {
  const b = String(base || '').trim();
  if (!b) return '';

  const p = String(path || '').trim();
  if (!p) return b;

  if (p.startsWith('http://') || p.startsWith('https://')) return p;
  if (p.startsWith('/')) return `${b}${p}`;
  return `${b}/${p}`;
}

function isTauri() {
  try {
    if (typeof window === 'undefined') return false;
    return !!(window.__TAURI__ && window.__TAURI__.core);
  } catch {
    return false;
  }
}

function normaliseRedirectPath(value) {
  const raw = String(value || '').trim();
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return '/comms';
  return raw;
}

function hasDesktopOpener() {
  try {
    return !!(window.__TAURI__ && window.__TAURI__.opener && typeof window.__TAURI__.opener.openUrl === 'function');
  } catch {
    return false;
  }
}

// Desktop app sign-in runs in the user's own browser (app=1): Google sign-in never happens inside the app
// window, so no Google account session is left in the app. The server then hands the sign-in back to the app
// with a one-time link (/auth/tauri/bridge -> <scheme>://auth?token= -> /comms/auth -> /auth/tauri/consume).
export function buildDesktopAuthUrl(redirectPath, appChallenge) {
  const authority = String(AUTHORITY_ORIGIN || '').trim() || getLocalOrigin();
  if (!authority) return '';
  try {
    const u = new URL(joinUrl(authority, '/auth/google'));
    u.searchParams.set('app', '1');
    u.searchParams.set('redirect_path', normaliseRedirectPath(redirectPath));
    if (appChallenge) u.searchParams.set('app_challenge', appChallenge);
    return u.toString();
  } catch {
    return '';
  }
}

// App-bound sign-in: the app keeps a secret verifier and sends only its SHA-256 (the challenge) to the browser.
// After Google sign-in the app collects the sign-in itself with the verifier, so it does not matter whether the
// OS deep link reaches an already-running app (a browser-launched second instance can lose its arguments).
const PENDING_KEY = 'vox.desktopLogin.v1';
const PENDING_TTL_MS = 10 * 60 * 1000;
let pollTimer = null;

function b64url(bytes) {
  let s = '';
  bytes.forEach((b) => { s += String.fromCharCode(b); });
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

export async function createAppChallenge() {
  const verifier = b64url(window.crypto.getRandomValues(new Uint8Array(32)));
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return { verifier, challenge: b64url(new Uint8Array(digest)) };
}

function readPending() {
  try {
    const p = JSON.parse(window.localStorage.getItem(PENDING_KEY) || 'null');
    if (p && typeof p.v === 'string' && Date.now() - Number(p.at) < PENDING_TTL_MS) return p;
  } catch {}
  try { window.localStorage.removeItem(PENDING_KEY); } catch {}
  return null;
}

export function stopDesktopLoginPoll() {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
}

export function startDesktopLoginPoll(intervalMs = 2000) {
  if (pollTimer || !isTauri()) return;
  if (!readPending()) return;
  pollTimer = setInterval(async () => {
    const pending = readPending();
    if (!pending) {
      clearInterval(pollTimer);
      pollTimer = null;
      return;
    }
    try {
      const r = await fetch('/auth/tauri/poll', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verifier: pending.v }),
      });
      if (r.status === 202) return; // not signed in yet
      try { window.localStorage.removeItem(PENDING_KEY); } catch {}
      clearInterval(pollTimer);
      pollTimer = null;
      if (r.ok) {
        const j = await r.json().catch(() => ({}));
        hardRedirect(j && typeof j.redirect === 'string' && j.redirect.startsWith('/') && !j.redirect.startsWith('//') ? j.redirect : '/comms/');
      }
    } catch {}
  }, intervalMs);
}

// Resume collecting after the app window reloads (for example when the deep link re-opens the app page).
try {
  if (typeof window !== 'undefined') setTimeout(() => startDesktopLoginPoll(), 0);
} catch {}

export function buildAuthUrl(redirectPath) {
  const localOrigin = getLocalOrigin();
  const authority = String(AUTHORITY_ORIGIN || '').trim();
  const base = authority || localOrigin;
  if (!base) return '';

  const authUrl = joinUrl(base, '/auth/google');

  try {
    const u = new URL(authUrl);

    if (localOrigin) u.searchParams.set('return_to', localOrigin);
    u.searchParams.set('redirect_path', normaliseRedirectPath(redirectPath));
    u.searchParams.delete('app');

    return u.toString();
  } catch {
    return authUrl;
  }
}

function hardRedirect(url) {
  if (!url) return;
  try {
    window.location.assign(url);
  } catch {
    try {
      window.location.href = url;
    } catch {}
  }
}

function tryPopup(url) {
  if (!url) return null;
  try {
    return window.open(url, 'voxemera_google_oauth', 'width=520,height=700,resizable=yes,scrollbars=yes');
  } catch {
    return null;
  }
}

export default function LoginButton(props) {
  const { children, label, className, onClick, redirectPath = '/comms', ...rest } = props;
  const text = children || label || 'Sign in with Google';

  const inFlightRef = useRef(false);
  const [waitingForBrowser, setWaitingForBrowser] = useState(false);

  async function handleClick(e) {
    try {
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    } catch {}

    if (inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      if (onClick) {
        try {
          onClick(e);
        } catch {}
      }

      if (e && e.defaultPrevented) return;

      const inTauri = isTauri();

      if (inTauri && hasDesktopOpener()) {
        let appChallenge = '';
        try {
          const pair = await createAppChallenge();
          window.localStorage.setItem(PENDING_KEY, JSON.stringify({ v: pair.verifier, at: Date.now() }));
          appChallenge = pair.challenge;
        } catch {}
        const desktopUrl = buildDesktopAuthUrl(redirectPath, appChallenge);
        if (desktopUrl && (await openExternalUrl(desktopUrl))) {
          setWaitingForBrowser(true);
          if (appChallenge) startDesktopLoginPoll();
          return;
        }
      }

      const url = buildAuthUrl(redirectPath);
      if (!url) return;

      if (inTauri) {
        // Older desktop shells without the opener: sign in inside the app window as before.
        hardRedirect(url);
        return;
      }

      const w = tryPopup(url);
      if (!w) hardRedirect(url);
    } finally {
      setTimeout(() => {
        inFlightRef.current = false;
      }, 800);
    }
  }

  return (
    <button
      type="button"
      className={className || ''}
      onClick={handleClick}
      title={waitingForBrowser ? 'Finish signing in in your browser; Voxemera will open again when you are done.' : undefined}
      {...rest}
    >
      {waitingForBrowser ? 'Continue in your browser…' : text}
    </button>
  );
}
